import { create } from "zustand";
import { persist } from "zustand/middleware";
import Cookies from "universal-cookie";

const cookies = new Cookies();

/* ----------------------------------------
   Helpers
---------------------------------------- */

const safeJSONParse = (value) => {
    try {
        return value ? JSON.parse(String(value)) : null;
    } catch {
        return null;
    }
};

const mergeByKey = (oldList = [], newList = [], key) => {
    if (!Array.isArray(newList)) return oldList;
    if (!key) return [...oldList, ...newList];

    const map = new Map();
    oldList.forEach((item) => {
        if (item?.[key] !== undefined) map.set(item[key], item);
    });

    let changed = false;
    const sizeBeforeNew = map.size;
    newList.forEach((item) => {
        if (item?.[key] !== undefined) {
            if (!changed && (!map.has(item[key]) || map.get(item[key]) !== item)) changed = true;
            map.set(item[key], item);
        }
    });

    if (!changed && map.size === sizeBeforeNew) return oldList;
    return Array.from(map.values());
};

const getPersistedStoreState = (persistedState) => {
    if (!persistedState || typeof persistedState !== "object") return {};
    if (persistedState.state && typeof persistedState.state === "object") {
        return persistedState.state;
    }
    return persistedState;
};

/* ----------------------------------------
   Base protected keys shared by all stores
---------------------------------------- */

const BASE_ACTION_KEYS = new Set([
    "getStates",
    "setStates",
    "getState",
    "setState",
    "setStateSmart",
    "deleteState",
    "clearAll",
    "setCookie",
    "getCookie",
    "deleteCookie",
    "resetAll",
    "resetMapCountry",
    "resetMapState",
]);

const filterWritable = (payload, actionKeys) =>
    Object.fromEntries(
        Object.entries(payload).filter(([k]) => !actionKeys.has(k))
    );

const actionsOnly = (state, actionKeys) =>
    Object.fromEntries(
        Object.entries(state).filter(([k]) => actionKeys.has(k))
    );

/* ----------------------------------------
   Factory
---------------------------------------- */

/**
 * Creates a persisted Zustand store following the standard map-store pattern.
 *
 * @param {string} storeName   - localStorage persist key
 * @param {object} initialState - plain data fields for this store
 * @returns {import('zustand').UseBoundStore}
 */
export function createMapStore(storeName, initialState) {
    const ACTION_KEYS = BASE_ACTION_KEYS;

    return create(
        persist(
            (set, get) => ({
                ...initialState,

                /* ---------- Global States ---------- */
                getStates: () =>
                    Object.fromEntries(
                        Object.entries(get()).filter(([k]) => !ACTION_KEYS.has(k))
                    ),

                setStates: (payload = {}) =>
                    set((s) => {
                        const filtered = filterWritable(payload, ACTION_KEYS);
                        const keys = Object.keys(filtered);
                        if (!keys.length) return s;
                        if (!keys.some((k) => !Object.is(s[k], filtered[k]))) return s;
                        return { ...s, ...filtered };
                    }),

                getState: (key) => get()[key],

                setState: (key, value) =>
                    set((s) => {
                        if (ACTION_KEYS.has(key) || Object.is(s[key], value)) return s;
                        return { ...s, [key]: value };
                    }),

                setStateSmart: (key, value, uniqueKey = null) =>
                    set((s) => {
                        if (ACTION_KEYS.has(key)) return s;
                        const merged = mergeByKey(s[key], value, uniqueKey);
                        if (Object.is(merged, s[key])) return s;
                        return { ...s, [key]: merged };
                    }),

                deleteState: (key) =>
                    set((s) => {
                        if (ACTION_KEYS.has(key)) return s;
                        const { [key]: _, ...rest } = s;
                        return rest;
                    }),

                clearAll: () =>
                    set((s) => actionsOnly(s, ACTION_KEYS), true),

                /* ---------- Cookies ---------- */
                setCookie: (key, value) =>
                    cookies.set(key, JSON.stringify(value), { path: "/" }),

                getCookie: (key) => safeJSONParse(cookies.get(key)),

                deleteCookie: (key) => cookies.remove(key, { path: "/" }),

                /* ---------- Reset ---------- */
                resetAll: () =>
                    set((s) => ({ ...actionsOnly(s, ACTION_KEYS), ...initialState }), true),

                resetMapCountry: () =>
                    set((s) => {
                        if (s.mapMode === "country" && s.mapState == null && s.mapCity == null) {
                            return s;
                        }
                        return { ...s, mapMode: "country", mapState: null, mapCity: null };
                    }),

                resetMapState: () =>
                    set((s) => {
                        if (s.mapMode === "state" && s.mapCity == null) return s;
                        return { ...s, mapMode: "state", mapCity: null };
                    }),
            }),
            {
                name: storeName,
                merge: (persistedState, currentState) => {
                    const persisted = getPersistedStoreState(persistedState);
                    const cleaned = filterWritable(persisted, ACTION_KEYS);
                    return { ...currentState, ...cleaned };
                },
                partialize: (state) => filterWritable(state, ACTION_KEYS),
            }
        )
    );
}
